import React, { useState, useEffect, useCallback } from 'react';
import JsSIP from 'jssip';
import {
  CALL_DIRECTION_INCOMING,
  CALL_DIRECTION_OUTGOING,
  CALL_STATUS_ACTIVE,
  CALL_STATUS_IDLE,
  CALL_STATUS_STARTING,
  CALL_STATUS_STOPPING,
  SIP_ERROR_TYPE_CONFIGURATION,
  SIP_ERROR_TYPE_CONNECTION,
  SIP_ERROR_TYPE_REGISTRATION,
  SIP_STATUS_CONNECTED,
  SIP_STATUS_CONNECTING,
  SIP_STATUS_DISCONNECTED,
  SIP_STATUS_ERROR,
  SIP_STATUS_REGISTERED,
} from './constants/statuses';
import { SipPhoneContext } from './context/SipPhoneContext';
import dummyLogger from './utils/dummyLogger';

const SIP_PROVIDER_AUDIO_ID = 'sip-provider-audio';

const SipPhoneProvider = ({
  host = null,
  port = null,
  pathname = '/ws',
  protocol = 'wss',
  user = null,
  password = null,
  callPrefix = '',
  autoRegister = true,
  autoAnswer = false,
  iceRestart = false,
  sessionTimersExpires = 120,
  extraHeaders = { register: [], invite: [] },
  iceServers = [],
  debug = false,
  children = null,
}) => {
  const [sipStatus, setSipStatus] = useState(SIP_STATUS_DISCONNECTED);
  const [sipErrorType, setSipErrorType] = useState(null);
  const [sipErrorMessage, setSipErrorMessage] = useState(null);

  const [rtcSession, setRtcSession] = useState(null);

  const [callStatus, setCallStatus] = useState(CALL_STATUS_IDLE);
  const [callDirection, setCallDirection] = useState(null);
  const [callInfo, setCallInfo] = useState(null);
  const [callError, setCallError] = useState(null);

  const [ua, setUa] = useState(null);
  const [logger, setLogger] = useState(dummyLogger);
  const [microphoneIsMuted, setMicrophoneIsMuted] = useState(false);

  const registerSip = () => {
    if (autoRegister) {
      throw new Error(
        'Calling registerSip is not allowed when autoRegister === true'
      );
    }

    if (sipStatus !== SIP_STATUS_CONNECTED) {
      throw new Error(
        `Calling registerSip is not allowed when sip status is ${sipStatus} (expected ${SIP_STATUS_CONNECTED})`
      );
    }

    return ua.register();
  };

  const unregisterSip = () => {
    if (autoRegister) {
      throw new Error(
        'Calling registerSip is not allowed when autoRegister === true'
      );
    }
    if (sipStatus !== SIP_STATUS_REGISTERED) {
      throw new Error(
        `Calling unregisterSip is not allowed when sip status is ${sipStatus} (expected ${SIP_STATUS_CONNECTED})`
      );
    }
    return ua.unregister();
  };

  const answerCall = () => {
    if (
      callStatus !== CALL_STATUS_STARTING ||
      callDirection !== CALL_DIRECTION_INCOMING
    ) {
      throw new Error(
        `Calling answerCall() is not allowed when call status is ${callStatus} and call direction is ${callDirection}  (expected ${CALL_STATUS_STARTING} and ${CALL_DIRECTION_INCOMING})`
      );
    }

    rtcSession.answer({
      mediaConstraints: {
        audio: true,
        video: false,
      },
      pcConfig: {
        iceServers: iceServers,
      },
    });
  };

  const startCall = (destination) => {
    if (!destination) {
      throw new Error(`Destination must be defined (${destination} given)`);
    }
    if (
      sipStatus !== SIP_STATUS_CONNECTED &&
      sipStatus !== SIP_STATUS_REGISTERED
    ) {
      throw new Error(
        `Calling startCall() is not allowed when sip status is ${sipStatus} (expected ${SIP_STATUS_CONNECTED} or ${SIP_STATUS_REGISTERED})`
      );
    }

    if (callStatus !== CALL_STATUS_IDLE) {
      throw new Error(
        `Calling startCall() is not allowed when call status is ${callStatus} (expected ${CALL_STATUS_IDLE})`
      );
    }

    const options = {
      extraHeaders: extraHeaders.invite,
      mediaConstraints: { audio: true, video: false },
      rtcOfferConstraints: { iceRestart },
      pcConfig: {
        iceServers,
      },
      sessionTimersExpires,
    };

    const num = destination.startsWith('#')
      ? destination
      : `${callPrefix}${destination}`;

    ua.call(num, options);
    setCallStatus(CALL_STATUS_STARTING);
    setCallError(null);
  };

  const stopCall = () => {
    setCallStatus(CALL_STATUS_STOPPING);
    ua.terminateSessions();
  };

  const toggleMuteMicrophone = () => {
    setMicrophoneIsMuted((prev) => !prev);
  };

  const sendDTMF = useCallback(
    (tone) => {
      if (rtcSession && rtcSession.isEstablished()) {
        rtcSession.sendDTMF(tone);
      }
    },
    [rtcSession]
  );

  const reconfigureDebug = () => {
    if (debug) {
      JsSIP.debug.enable('JsSIP:*');
      setLogger(console);
    } else {
      JsSIP.debug.disable('JsSIP:*');
      setLogger(dummyLogger);
    }
  };

  const reinitializeJsSIP = () => {
    if (ua) {
      ua.stop();
    }

    if (!host || !port || !user) {
      setSipStatus(SIP_STATUS_DISCONNECTED);
      setSipErrorType(null);
      setSipErrorMessage(null);
      return;
    }

    try {
      const socket = new JsSIP.WebSocketInterface(
        `${protocol}://${host}:${port}${pathname}`
      );

      const newUa = new JsSIP.UA({
        uri: `sip:${user}@${host}`,
        password,
        sockets: [socket],
        register: autoRegister,
      });

      setUa(newUa);
    } catch (error) {
      logger.debug('Error', error.message, error);
      setSipStatus(SIP_STATUS_ERROR);
      setSipErrorType(SIP_ERROR_TYPE_CONFIGURATION);
      setSipErrorMessage(error.message);
      return;
    }
  };

  // Обработчики событий для ua
  useEffect(() => {
    if (ua) {
      ua.on('connecting', () => {
        logger.debug('UA "connecting" event');

        setSipStatus(SIP_STATUS_CONNECTING);
        setSipErrorType(null);
        setSipErrorMessage(null);
      });

      ua.on('connected', () => {
        logger.debug('UA "connected" event');

        setSipStatus(SIP_STATUS_CONNECTED);
        setSipErrorType(null);
        setSipErrorMessage(null);
      });

      ua.on('disconnected', () => {
        logger.debug('UA "disconnected" event');

        setSipStatus(SIP_STATUS_ERROR);
        setSipErrorType(SIP_ERROR_TYPE_CONNECTION);
        setSipErrorMessage('disconnected');
      });

      ua.on('registered', (data) => {
        logger.debug('UA "registered" event', data);

        setSipStatus(SIP_STATUS_REGISTERED);
        setCallStatus(CALL_STATUS_IDLE);
      });

      ua.on('unregistered', () => {
        logger.debug('UA "unregistered" event');

        if (ua.isConnected()) {
          setSipStatus(SIP_STATUS_CONNECTED);
        } else {
          setSipStatus(SIP_STATUS_DISCONNECTED);
        }

        setCallStatus(CALL_STATUS_IDLE);
        setCallDirection(null);
      });

      ua.on('registrationFailed', (data) => {
        logger.debug('UA "registrationFailed" event');

        setSipStatus(SIP_STATUS_ERROR);
        setSipErrorType(SIP_ERROR_TYPE_REGISTRATION);
        setSipErrorMessage(data);
      });

      ua.on('newRTCSession', ({ originator, session, request: rtcRequest }) => {
        // identify call direction
        if (originator === 'local') {
          setCallDirection(CALL_DIRECTION_OUTGOING);
          setCallStatus(CALL_STATUS_STARTING);
          setCallInfo({
            id: rtcRequest.call_id,
            phoneNumber: rtcRequest.to._uri._user,
          });
        } else if (originator === 'remote') {
          setCallDirection(CALL_DIRECTION_INCOMING);
          setCallStatus(CALL_STATUS_STARTING);
          setCallInfo({
            id: rtcRequest.call_id,
            phoneNumber: rtcRequest.from._uri._user,
          });
        }

        // Avoid if busy or other incoming
        if (rtcSession) {
          logger.debug('incoming call replied with 486 "Busy Here"');
          session.terminate({
            status_code: 486,
            reason_phrase: 'Busy Here',
          });
          return;
        }

        setRtcSession(session);

        if (callDirection === CALL_DIRECTION_INCOMING && autoAnswer) {
          logger.log('Answer auto ON');
          answerCall();
        } else if (callDirection === CALL_DIRECTION_INCOMING && !autoAnswer) {
          logger.log('Answer auto OFF');
        } else if (callDirection === CALL_DIRECTION_OUTGOING) {
          logger.log('OUTGOING call');
        }
      });

      const extraHeadersRegister = extraHeaders.register || [];
      if (extraHeadersRegister.length) {
        ua.registrator().setExtraHeaders(extraHeadersRegister);
      }
      ua.start();

      return () => {
        ua.stop();
      };
    }
  }, [ua]);

  // Обработчики событий для rtcSession
  useEffect(() => {
    if (rtcSession) {
      rtcSession.on('accepted', () => {
        const remoteAudio = document.getElementById(SIP_PROVIDER_AUDIO_ID);

        if (remoteAudio) {
          [remoteAudio.srcObject] = rtcSession.connection.getRemoteStreams();

          const played = remoteAudio.play();

          if (typeof played !== 'undefined') {
            played
              .catch(() => {
                /**/
              })
              .then(() => {
                setTimeout(() => {
                  remoteAudio.play();
                }, 2000);
              });

            setCallStatus(CALL_STATUS_ACTIVE);
            return;
          }

          setTimeout(() => {
            remoteAudio.play();
          }, 2000);

          setCallStatus(CALL_STATUS_ACTIVE);
        }
      });

      rtcSession.on('ended', () => {
        setRtcSession(null);
        setCallStatus(CALL_STATUS_IDLE);
        setCallDirection(null);
      });

      rtcSession.on('failed', (data) => {
        setRtcSession(null);
        setCallStatus(CALL_STATUS_IDLE);
        setCallDirection(null);
        setCallInfo(null);
        setCallError(data);
      });
    }
  }, [rtcSession]);

  // Включение/отключение микрофона
  useEffect(() => {
    if (rtcSession) {
      microphoneIsMuted
        ? rtcSession.mute({ audio: true, video: false })
        : rtcSession.unmute({ audio: true, video: false });
    }
  }, [rtcSession, microphoneIsMuted]);

  // Установка audio для разговора
  useEffect(() => {
    const audio = document.getElementById(SIP_PROVIDER_AUDIO_ID);

    if (audio) {
      throw new Error(
        `Creating two SipProviders in one application is forbidden. If that's not the case ` +
          `then check if you're using "sip-provider-audio" as id attribute for any existing ` +
          `element`
      );
    }

    const remoteAudio = document.createElement('audio');
    remoteAudio.id = SIP_PROVIDER_AUDIO_ID;
    document.body.appendChild(remoteAudio);

    return () => {
      remoteAudio.parentNode.removeChild(remoteAudio);
    };
  }, []);

  // Инициализация JsSip
  useEffect(() => {
    reinitializeJsSIP();
  }, [host, port, pathname, user, password, autoRegister, protocol]);

  useEffect(() => {
    reconfigureDebug();
  }, [debug]);

  const value = {
    sip: {
      status: sipStatus,
      errorType: sipErrorType,
      errorMessage: sipErrorMessage,
    },
    call: {
      info: callInfo,
      setInfo: setCallInfo,
      status: callStatus,
      direction: callDirection,
      error: callError,
    },
    registerSip,
    unregisterSip,

    answerCall,
    startCall,
    stopCall,
    microphoneIsMuted,
    toggleMuteMicrophone,
    sendDTMF,
  };

  return (
    <SipPhoneContext.Provider value={value}>
      {children}
    </SipPhoneContext.Provider>
  );
};

export default SipPhoneProvider;
